import { CSSProperties, ReactNode } from 'react'
import useScreen from 'Hooks/useScreen'
import { styx, clsx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'
import Banner from 'elements/Banner'
import { WorkspaceTheme } from './AssistantWorkspaceWrapper'

export interface ActionButton {
    /** Button text */
    text: string;
    /** Click handler */
    onClick: () => void;
    /** Button variant */
    variant?: 'primary' | 'outlined' | 'text';
    /** Icon name */
    icon?: string;
    /** Disabled state */
    disabled?: boolean;
    /** Loading state */
    loading?: boolean;
    /** Custom style overrides */
    style?: CSSProperties;
    /** Custom className */
    className?: string;
}

interface AssistantWorkspaceActionsProps {
    /** Icon for the top section */
    icon?: string;
    /** Title for the top section */
    title?: string;
    /** Description text below title */
    description?: string;
    /** Array of action buttons */
    buttons?: ActionButton[];
    /** Whether to show in sticky position */
    sticky?: boolean;
    /** Custom className */
    className?: string;
    /** Custom style */
    style?: CSSProperties;
    /** Show only if condition is met */
    showIf?: boolean;
    /** Custom content to show instead of buttons */
    customContent?: ReactNode;
    /** Whether to show custom content instead of buttons */
    showCustomContent?: boolean;
}

const AssistantWorkspaceActions = ({
    icon,
    title,
    description,
    buttons = [],
    sticky = true,
    className,
    style,
    showIf = true,
    customContent,
    showCustomContent = false
}: AssistantWorkspaceActionsProps) => {
    const { mobileView, mStyle } = useScreen()

    if (!showIf) {
        return null
    }

    const hasAnyContent = title || description || buttons.length > 0 || (showCustomContent && customContent)

    if (!hasAnyContent) {
        return null
    }

    const renderActionButton = (action: ActionButton, index: number) => {
        return (
            <ButtonSymphony
                key={index}
                className={clsx('workspaceActionButton', action.className)}
                variant={action.variant || 'outlined'}
                onClick={action.onClick}
                size="large"
                disabled={action.disabled || action.loading}
                style={styx(sty.actionButton, action.style)}
            >
                {action.loading ? (
                    <Icon className="loadingIcon" icon="loader" size={16} />
                ) : null}
                {action.text}
            </ButtonSymphony>
        )
    }

    const renderHeader = () => {
        if (!title && !description) return null

        return (
            <div className="workspaceActionsHeader" style={sty.actionsHeader}>
                {title && (
                    <div className="actionsTitle" style={sty.actionsTitle}>
                        {icon && (
                            <Icon className="titleIcon" icon={icon} size={20} />
                        )}
                        {title}
                    </div>
                )}
                {description && (
                    <div className="actionsDescription" style={sty.actionsDescription}>
                        {description}
                    </div>
                )}
            </div>
        )
    }

    const renderContent = () => {
        // Show custom content if enabled
        if (showCustomContent && customContent) {
            return (
                <div className="workspaceCustomContent" style={sty.customContentContainer}>
                    {customContent}
                </div>
            )
        }

        // Show buttons if no custom content
        if (buttons.length === 0) return null

        return (
            <div
                className="workspaceActions"
                style={mStyle(sty, "buttonsContainer")}
            >
                {buttons.map((button, index) => renderActionButton(button, index))}
            </div>
        )
    }

    return (
        <div
            className={clsx(
                'assistantWorkspaceActions',
                sticky && 'sticky',
                mobileView && 'mobile',
                className
            )}
            style={styx(
                sty.container,
                sticky && sty.container_sticky,
                mobileView && sty.container_mobile,
                style
            )}
        >
                    <div className="workspaceActionsInner" style={mStyle(sty, "inner")}>
            {renderHeader()}
            {renderContent()}
        </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        width: '100%',
        backgroundColor: WorkspaceTheme.surfaceLight,
        borderTop: `1px solid ${WorkspaceTheme.border}`,
        zIndex: 100,
    },
    container_sticky: {
        position: 'sticky',
        bottom: 0,
        boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.3)',
    },
    container_mobile: {
        borderRadius: 0,
    },
    inner: {
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 800,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
    },

    inner_mobile: {
        padding: 16,
        width: '100%',
    },

    // Header
    actionsHeader: {
        textAlign: 'center',
    },
    actionsTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        ...Sty.text18bold,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 8,
    },
    actionsDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },

    // Buttons
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        width: '100%',
    },
    buttonsContainer_mobile: {
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    actionButton: {
        gap: 6,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },

    // Custom content
    customContentContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
}

export default AssistantWorkspaceActions 